import React, { FC, useState } from 'react';
import { Alert, Box, Button, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';

type OAuthAppCredentialsDialogProps = {
    open: boolean;
    clientId: string;
    clientSecret: string | null;
    onClose: () => void;
};

const OAuthAppCredentialsDialog: FC<OAuthAppCredentialsDialogProps> = ({
    open,
    clientId,
    clientSecret,
    onClose,
}: OAuthAppCredentialsDialogProps) => {
    const { t: translate } = useTranslation(['app-settings', 'common']);
    const [copiedField, setCopiedField] = useState<'id' | 'secret' | null>(null);

    const handleCopy = async (value: string, field: 'id' | 'secret') => {
        await navigator.clipboard.writeText(value);
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 1500);
    };

    return (
        <DHDialog
            open={open}
            onClose={onClose}
            disableBackdropClick
            maxWidth="sm"
            fullWidth
            title={translate('app-settings:OAUTH_APP_CREDENTIALS_TITLE')}
            actions={
                <Button onClick={onClose} variant="contained">
                    {translate('app-settings:API_CLIENT_SECRET_DISMISS')}
                </Button>
            }
        >
            {clientSecret && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {translate('app-settings:OAUTH_APP_SECRET_WARNING')}
                </Alert>
            )}
            <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                    {translate('app-settings:OAUTH_APP_CLIENT_ID')}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                    <TextField value={clientId} fullWidth size="small" InputProps={{ readOnly: true }} />
                    <Tooltip title={copiedField === 'id' ? translate('common:COPIED') : translate('common:COPY')}>
                        <IconButton onClick={() => handleCopy(clientId, 'id')}>
                            <ContentCopyIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            {clientSecret ? (
                <Box>
                    <Typography variant="subtitle2" gutterBottom>
                        {translate('app-settings:OAUTH_APP_CLIENT_SECRET')}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                        <TextField value={clientSecret} fullWidth size="small" InputProps={{ readOnly: true }} />
                        <Tooltip
                            title={copiedField === 'secret' ? translate('common:COPIED') : translate('common:COPY')}
                        >
                            <IconButton onClick={() => handleCopy(clientSecret, 'secret')}>
                                <ContentCopyIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            ) : (
                <Alert severity="info">{translate('app-settings:OAUTH_APP_PUBLIC_CLIENT_INFO')}</Alert>
            )}
        </DHDialog>
    );
};

export default OAuthAppCredentialsDialog;
